import React, { useEffect, useState, useContext } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Card, CardBody, Input } from "@windmill/react-ui";
import { useTranslation } from "react-i18next";
import Title from "components/form/Title";
import LabelArea from "components/form/LabelArea";
import DrawerButton from "components/form/DrawerButton";
import UserServices from "services/UserServices";
import { notifyError, notifySuccess } from "utils/toast";
import { SidebarContext } from "context/SidebarContext";

const FieldError = ({ message }) => {
  if (!message) return null;
  return <div className="text-red-500 text-sm mt-1">{message}</div>;
};

const AddUserDrawer = ({
  id,
  isLoading,
  setIsLoading,
  setIsCheck,
  onDrawerClose,
}) => {
  const { t } = useTranslation();
  const { closeDrawer } = useContext(SidebarContext);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setTaxNumber("");
    setFieldErrors({});
  };

  const firstErrorMessage = (errors) => {
    if (!errors || typeof errors !== "object") return null;
    const firstKey = Object.keys(errors)[0];
    const value = errors[firstKey];
    return Array.isArray(value) ? value[0] : value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const userData = {
      name,
      email,
      phone,
      tax_number: taxNumber,
    };

    try {
      setIsLoading(true);
      const res = await UserServices.addUser(userData);
      closeDrawer();
      notifySuccess(res.message || "User created successfully");
      resetForm();
      localStorage.setItem("newUser", JSON.stringify(res.data));
      if (typeof setIsCheck === "function") {
        setIsCheck([]);
      }
      if (typeof onDrawerClose === "function") {
        onDrawerClose();
      }
    } catch (error) {
      const data = error?.response?.data;
      const errors = data?.errors || {};
      setFieldErrors(errors);

      const message =
        firstErrorMessage(errors) ||
        data?.message ||
        data?.error ||
        error?.message ||
        "Failed to create user";

      notifyError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    resetForm();
    if (typeof setIsCheck === "function") {
      setIsCheck([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="w-full relative p-6 border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        {id ? (
          <Title
            title={t("UpdateStaff")}
            description={t("UpdateStaffdescription")}
          />
        ) : (
          <Title
            title={t("Add User")}
            description={t("AddUserdescription")}
          />
        )}
      </div>
      <Scrollbars className="w-full md:w-7/12 lg:w-8/12 xl:w-8/12 relative dark:bg-gray-700 dark:text-gray-200">
        <Card className="overflow-y-scroll flex-grow scrollbar-hide w-full max-h-full">
          <CardBody>
            <form onSubmit={handleSubmit}>
              <div className="px-6 pt-8 flex-grow scrollbar-hide w-full max-h-full pb-40">
                <div className="grid grid-cols-6 gap-3 md:gap-5 xl:gap-6 lg:gap-6 mb-6">
                  <LabelArea label={"name "} />
                  <div className="col-span-8 sm:col-span-4">
                    <Input
                      className="border h-12 text-sm focus:outline-none block w-full bg-gray-100 dark:bg-white border-transparent focus:bg-white"
                      name="name"
                      type="text"
                      placeholder={"name"}
                      onChange={(e) => setName(e.target.value)}
                      value={name}
                      required
                    />
                    <FieldError message={fieldErrors.name?.[0]} />
                  </div>
                </div>

                <div className="grid grid-cols-6 gap-3 md:gap-5 xl:gap-6 lg:gap-6 mb-6">
                  <LabelArea label={"Phone "} />
                  <div className="col-span-8 sm:col-span-4">
                    <Input
                      className="border h-12 text-sm focus:outline-none block w-full bg-gray-100 dark:bg-white border-transparent focus:bg-white"
                      name="phone"
                      type="text"
                      placeholder={"Phone"}
                      onChange={(e) => setPhone(e.target.value)}
                      value={phone}
                      required
                    />
                    <FieldError message={fieldErrors.phone?.[0]} />
                  </div>
                </div>

                <div className="grid grid-cols-6 gap-3 md:gap-5 xl:gap-6 lg:gap-6 mb-6">
                  <LabelArea label={"Email "} />
                  <div className="col-span-8 sm:col-span-4">
                    <Input
                      className="border h-12 text-sm focus:outline-none block w-full bg-gray-100 dark:bg-white border-transparent focus:bg-white"
                      name="email"
                      type="email"
                      placeholder={"Email"}
                      onChange={(e) => setEmail(e.target.value)}
                      value={email}
                      required
                    />
                    <FieldError message={fieldErrors.email?.[0]} />
                  </div>
                </div>

                <div className="grid grid-cols-6 gap-3 md:gap-5 xl:gap-6 lg:gap-6 mb-6">
                  <LabelArea label={"Code TVA "} />
                  <div className="col-span-8 sm:col-span-4">
                    <Input
                      className="border h-12 text-sm focus:outline-none block w-full bg-gray-100 dark:bg-white border-transparent focus:bg-white"
                      name="tax_number"
                      type="text"
                      placeholder={"Code TVA"}
                      onChange={(e) => setTaxNumber(e.target.value)}
                      value={taxNumber}
                      required
                    />
                    <FieldError message={fieldErrors.tax_number?.[0]} />
                  </div>
                </div>
              </div>
              <DrawerButton id={id} title="User" />
            </form>
          </CardBody>
        </Card>
      </Scrollbars>
    </>
  );
};

export default AddUserDrawer;
